import { create } from "zustand";

export type ThemeMode = "light" | "dark";
export type ThemePreference = ThemeMode | "system";

const STORAGE_KEY = "sonows-theme";

function readStoredPreference(): ThemePreference {
  if (typeof window === "undefined") return "light";
  const stored = window.localStorage.getItem(STORAGE_KEY);
  if (stored === "light" || stored === "dark" || stored === "system") {
    return stored;
  }
  return "system";
}

function osPrefersDark(): boolean {
  if (typeof window === "undefined" || !window.matchMedia) return false;
  return window.matchMedia("(prefers-color-scheme: dark)").matches;
}

function resolveTheme(pref: ThemePreference): ThemeMode {
  if (pref === "system") return osPrefersDark() ? "dark" : "light";
  return pref;
}

interface ThemeState {
  /** What's currently painted: always "light" or "dark", never "system". */
  theme: ThemeMode;
  /** What the user picked. "system" tracks OS preference. */
  preference: ThemePreference;
  toggleTheme: () => void;
  setTheme: (t: ThemeMode) => void;
  setPreference: (p: ThemePreference) => void;
  /** Re-resolve from OS — called by ThemeProvider's matchMedia listener. */
  syncFromSystem: () => void;
}

const initialPreference = readStoredPreference();

export const useThemeStore = create<ThemeState>((set, get) => ({
  theme: resolveTheme(initialPreference),
  preference: initialPreference,
  toggleTheme: () => {
    const next: ThemeMode = get().theme === "light" ? "dark" : "light";
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, next);
    }
    set({ theme: next, preference: next });
  },
  setTheme: (t) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, t);
    }
    set({ theme: t, preference: t });
  },
  setPreference: (p) => {
    if (typeof window !== "undefined") {
      window.localStorage.setItem(STORAGE_KEY, p);
    }
    set({ preference: p, theme: resolveTheme(p) });
  },
  syncFromSystem: () => {
    if (get().preference !== "system") return;
    set({ theme: osPrefersDark() ? "dark" : "light" });
  },
}));
